import React, { useEffect, useMemo, useState } from 'react'
import { CardBasic, CardHeaderWithActions, MwButton, MwInputText, MwSelector, MwSpinner, MwSpinnerButton, NoDataFound } from '../components/ui';
import { useCurrent, useFetch, usePost } from '../hooks';
import RatingList from './rating/RatingList';
import RatingBox from '../components/ui/RatingBox';
import { useTranslation } from 'react-i18next';
import { HiOutlineDocumentReport, HiOutlineFilter } from 'react-icons/hi';

const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getCurrentMonthDateRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
        startDate: formatDateInput(start),
        endDate: formatDateInput(end)
    };
};

const Reports = () => {
    const [t] = useTranslation('global')
    const [mode, setMode] = useState('Listing');
    const currentMonthRange = getCurrentMonthDateRange();

    const [serviceSelected, setServiceSelected] = useState();
    const [startDate, setStartDate] = useState(currentMonthRange.startDate);
    const [endDate, setEndDate] = useState(currentMonthRange.endDate);
    const [complaintStatus, setComplaintStatus] = useState({ label: t('reports.complaintStatusAll'), value: 'all' });
    const [ratingType, setRatingType] = useState({ label: t('reports.ratingTypeAll'), value: 'all' });

    const [serviceRatingSum, setServiceRatingSum] = useState(0);
    const [employeeRatingSum, setEmployeeRatingSum] = useState(0);

    const { currentTenantId, currentCompanyId, currentBranchId } = useCurrent();
    const servicesUrl = useMemo(() => {
        if (!currentTenantId || !currentCompanyId || !currentBranchId) return null;
        return `${process.env.REACT_APP_RATING_API_BASE_URL}/services/${currentTenantId}/${currentCompanyId}/${currentBranchId}`;
    }, [currentTenantId, currentCompanyId, currentBranchId]);
    const { data: servicesResult, loading: servicesLoading, refreshHandler, error: servicesError } = useFetch(servicesUrl, Boolean(servicesUrl));

    const { data: postResult, loading: postLoading, postData } = usePost();

    useEffect(() => {
        if (servicesError) {
            console.error('Services API Error:', servicesError);
            return;
        }

        if (servicesResult) {
            if (servicesResult.status === 0) {
                console.error('Services API Error:', servicesResult.message);
                return;
            }

            if (servicesResult.data && servicesResult.data.length > 0) {
                let _rand = Math.floor(Math.random() * servicesResult.data.length);
                const _selected = servicesResult.data[_rand];
                if (_selected && _selected.serviceName) {
                    setServiceSelected({ label: _selected.serviceName, value: _selected });
                }
            }
        }
    }, [servicesResult, servicesError]);

    useEffect(() => {
        if (postResult) {
            const _serviceRatingSum = postResult?.data.reduce((a, b) => a + b.ratingService, 0);
            const _rateService = _serviceRatingSum > 0 ? Math.round((_serviceRatingSum / (postResult?.data?.length * 5)) * 5) : 0;
            setServiceRatingSum(_rateService);

            const _employeeRatingSum = postResult?.data.reduce((a, b) => a + (b.ratingEmployee || b.ratingService), 0);
            const _rateEmployee = _employeeRatingSum > 0 ? Math.round((_employeeRatingSum / (postResult?.data?.length * 5)) * 5) : 0;
            setEmployeeRatingSum(_rateEmployee);
        }
    }, [postResult]);

    const submitReport = () => {
        if (!currentTenantId || !currentCompanyId || !currentBranchId || !serviceSelected?.value?._id) return;
        const _url = `${process.env.REACT_APP_RATING_API_BASE_URL}/filter/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${serviceSelected?.value?._id}`;
        if (startDate && endDate) {
            const _data = {
                dateStart: new Date(startDate),
                dateEnd: new Date(endDate),
                complaintStatus: complaintStatus?.value || 'all',
                ratingType: ratingType?.value || 'all'
            }
            postData(_url, _data);
            refreshHandler();
        }
    }

    return (
        <div className='flex flex-col gap-6 animate-fadeIn'>
            <div className='flex justify-between items-center'>
                <div>
                    <h1 className='text-2xl font-bold text-slate-800 flex items-center gap-2'>
                        <HiOutlineDocumentReport className='text-amber-500' />
                        {t('reports.title')}
                    </h1>
                    <p className='text-sm text-slate-500 mt-1'>{t('reports.subtitle') || 'Generate detailed performance reports'}</p>
                </div>
            </div>

            <div className='bg-white p-6 rounded-3xl shadow-sm border border-slate-100'>
                <div className='flex items-center gap-2 mb-6 pb-4 border-b border-slate-50'>
                    <HiOutlineFilter className='text-slate-400' />
                    <h2 className='text-sm font-semibold text-slate-700 uppercase tracking-wider'>{t('reports.filters')}</h2>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-5 gap-6'>
                    <div className='space-y-1'>
                        <MwSelector
                            label={t('reports.selectService')}
                            initalValue={serviceSelected?.label || t('reports.selectServicePlaceholder')}
                            dataType={'labelValue'}
                            withAddNew={false}
                            setSelectedItem={setServiceSelected}
                            selectedItem={serviceSelected}
                            _data={servicesResult?.data?.filter(service => service && service.serviceName).map(service => ({
                                label: service.serviceName,
                                value: service,
                            })) || []}
                        />
                    </div>
                    <div className='space-y-1'>
                        <MwSelector
                            label={t('reports.complaintStatusLabel')}
                            initalValue={complaintStatus?.label || t('reports.complaintStatusAll')}
                            dataType={'labelValue'}
                            withAddNew={false}
                            setSelectedItem={setComplaintStatus}
                            selectedItem={complaintStatus}
                            _data={[
                                { label: t('reports.complaintStatusAll'), value: 'all' },
                                { label: t('reports.complaintStatusCompleted'), value: 'completed' },
                                { label: t('reports.complaintStatusIncomplete'), value: 'incomplete' }
                            ]}
                        />
                    </div>
                    <div className='space-y-1'>
                        <MwSelector
                            label={t('reports.ratingTypeLabel')}
                            initalValue={ratingType?.label || t('reports.ratingTypeAll')}
                            dataType={'labelValue'}
                            withAddNew={false}
                            setSelectedItem={setRatingType}
                            selectedItem={ratingType}
                            _data={[
                                { label: t('reports.ratingTypeAll'), value: 'all' },
                                { label: t('reports.ratingTypeComplaint'), value: 'complaint' },
                                { label: t('reports.ratingTypeRating'), value: 'rating' }
                            ]}
                        />
                    </div>
                    <div className='space-y-1'>
                        <MwInputText
                            label={t('reports.startDate')}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            inputType='date'
                        />
                    </div>
                    <div className='space-y-1'>
                        <MwInputText
                            label={t('reports.endDate')}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            inputType='date'
                        />
                    </div>
                </div>

                <div className='mt-8 pt-4 border-t border-slate-50 flex justify-end'>
                    <button
                        onClick={() => submitReport()}
                        disabled={postLoading}
                        className='px-8 py-3 bg-slate-900 text-white rounded-2xl font-bold transition-all duration-300 hover:bg-slate-800 hover:shadow-lg disabled:opacity-50 flex items-center gap-2'
                    >
                        {postLoading ? <MwSpinnerButton withLabel={false} /> : t('reports.prepareReport')}
                    </button>
                </div>
            </div>

            {postLoading && (
                <div className='py-20 flex flex-col items-center justify-center bg-white/50 rounded-3xl border border-dashed border-slate-200'>
                    <MwSpinner />
                    <p className='text-sm text-slate-500 mt-4 animate-pulse'>{t('reports.processing')}</p>
                </div>
            )}

            {postResult && !postLoading && (
                <div className='animate-slideUp'>
                    {postResult?.data?.length === 0 ? (
                        <div className='bg-white p-12 rounded-3xl border border-slate-100 text-center'>
                            <NoDataFound msg={t('reports.noResults')} />
                        </div>
                    ) : (
                        <div className='bg-white p-8 rounded-3xl border border-slate-100 shadow-sm'>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-10'>
                                <div className='bg-amber-50/50 p-6 rounded-2xl border border-amber-100/50 flex flex-col items-center gap-3'>
                                    <span className='text-xs font-bold text-amber-700 uppercase tracking-widest'>{t('reports.totalServiceRating')}</span>
                                    <RatingBox rate={serviceRatingSum} size={40} />
                                    <span className='text-2xl font-black text-amber-900'>{serviceRatingSum}/5</span>
                                </div>
                                <div className='bg-blue-50/50 p-6 rounded-2xl border border-blue-100/50 flex flex-col items-center gap-3'>
                                    <span className='text-xs font-bold text-blue-700 uppercase tracking-widest'>{t('reports.totalEmployeeRating')}</span>
                                    <RatingBox rate={employeeRatingSum} size={40} />
                                    <span className='text-2xl font-black text-blue-900'>{employeeRatingSum}/5</span>
                                </div>
                            </div>

                            <div className='border-t border-slate-50 pt-8'>
                                <h3 className='text-lg font-bold text-slate-800 mb-6 px-2'>{t('reports.detailedFeedback')}</h3>
                                <RatingList loading={postLoading} data={postResult} />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default Reports
